import React, { useEffect, useRef, useState } from 'react';
import { getMapData, show3dMap } from '@mappedin/mappedin-js';
import { BlueDot } from '@mappedin/blue-dot';
import '@mappedin/mappedin-js/lib/index.css';

const MappedInTestFile = ({
    spaceName,
    locationName,
    brandColor,
    onError,
    videoDuration = 30000, // Default 30 seconds in milliseconds
    autoStart = true,
    videoPlaying

}) => {
    const mapRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [blueDot, setBlueDot] = useState(null);
    const [mapView, setMapView] = useState(null);
    const [mapData, setMapData] = useState(null);
    const [totalMarkers, setTotalMarkers] = useState(0);
    const [markerInterval, setMarkerInterval] = useState(0);
    const [animationStarted, setAnimationStarted] = useState(false);
    const [pathVisible, setPathVisible] = useState(false);
    const [directions, setDirections] = useState(null);

    const MAPPEDIN_CONFIG = {
        key: "mik_gxWE7PUdHxoK9qNlr555107ac",
        secret: "mis_hvx40sGJZ2cuyzSDEHv55PtldjuszH20dVqhRkBV8R597271073",
        mapId: "69709560a8a681000bf10352"
    };

    const initMap = async () => {
        try {
            setIsLoading(true);
            console.log('Initializing MappedIn map with synchronized animation...');

            // Get map data
            const options = {
                key: MAPPEDIN_CONFIG.key,
                secret: MAPPEDIN_CONFIG.secret,
                mapId: MAPPEDIN_CONFIG.mapId,
            };

            const data = await getMapData(options);
            setMapData(data);
            console.log('Map data loaded:', data);

            // Initialize 3D map
            const view = await show3dMap(document.getElementById('mappedin-test-map'), data);
            setMapView(view);
            console.log('3D Map created:', view);

            // Add labels to all spaces
            data.getByType("space").forEach((space) => {
                if (space.name) {
                    view.Labels.add(space, space.name, { interactive: true });
                }
            });

            // Setup navigation and calculate timing
            if (spaceName && spaceName.trim()) {
                await setupNavigationPath(view, data, spaceName.trim());
            } else {
                await setupNavigationPath(view, data, "Kiosk");
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error initializing MappedIn map:', error);
            setError(error.message);
            setIsLoading(false);

            if (onError) {
                onError();
            }
        }
    };

    const setupNavigationPath = async (view, data, targetSpaceName) => {
        try {
            console.log('Setting up navigation path from Kiosk to:', targetSpaceName);

            const spacesData = data.getByType("space");
            const startGate = spacesData.find((space) => space.name === "Kiosk");
            const endGate = spacesData.find((space) =>
                space.name && space.name.toLowerCase().trim() === targetSpaceName.toLowerCase().trim()
            );

            if (!startGate || !endGate) {
                console.warn("Could not find start or end gates");
                return;
            }

            // Get directions
            const directionsResult = await view.getDirections(startGate, endGate, {
                smoothing: true,
            });

            if (!directionsResult) {
                console.warn("No directions found");
                return;
            }

            setDirections(directionsResult);

            // Calculate total markers and timing
            const markersCount = directionsResult.instructions.length;
            setTotalMarkers(markersCount);

            // Calculate interval between markers based on video duration


            const intervalTime = videoDuration / markersCount;
            setMarkerInterval(intervalTime);

            console.log(`Total markers: ${markersCount}`);
            console.log(`Video duration: ${videoDuration}ms`);
            console.log(`Marker interval: ${intervalTime}ms`);

            // Set camera to show the full route
            view.Camera.focusOn(directionsResult.coordinates, {
                screenOffsets: { bottom: 50, left: 50, top: 50, right: 50 },
            });

            // Show the path first
            view.Navigation.draw(directionsResult);
            setPathVisible(true);

            setCurrentLocation(endGate);
            console.log('Navigation path setup completed');

            // Auto-start animation if enabled
            console.log('Vdeo is plkayed', videoPlaying)

            // if (videoPlaying) {
                // setTimeout(() => {
                    startSynchronizedAnimation(view, data, targetSpaceName, directionsResult);
                // }, 2000);
            // }

        } catch (error) {
            console.error('Error setting up navigation path:', error);
        }
    };

    const initializeBlueDot = (view) => {
        try {
            if (blueDot) {
                blueDot.destroy();
            }

            const blueDotInstance = new BlueDot(view);
            blueDotInstance.enable({ radius: 5 });
            blueDotInstance.follow("position-only");
            setBlueDot(blueDotInstance);
            console.log('Blue Dot initialized');
            return blueDotInstance;
        } catch (error) {
            console.warn('Could not initialize Blue Dot:', error);
            return null;
        }
    };

    const startSynchronizedAnimation = async (view, data, targetSpaceName, directionsData) => {
        console.log('🎬 Starting synchronized animation for:', targetSpaceName);

        if (animationStarted) {
            console.log('⚠️ Animation already started');
            return;
        }

        setAnimationStarted(true);

        // Initialize BlueDot for animation
        const blueDotInstance = initializeBlueDot(view);

        if (!blueDotInstance) {
            console.error('❌ Could not initialize BlueDot for animation');
            setAnimationStarted(false);
            return;
        }

        try {
            // Clear existing markers
            view.Markers.removeAll();

            // Add destination flag at target location
            const spacesData = data.getByType("space");
            const endGate = spacesData.find((space) =>
                space.name && space.name.toLowerCase().trim() === targetSpaceName.toLowerCase().trim()
            );

            if (endGate) {
                const flagTemplate = `
                    <div style="
                        background: linear-gradient(135deg, #ff6b6b, #ee5a24);
                        color: white;
                        padding: 8px 12px;
                        border-radius: 20px;
                        font-size: 14px;
                        font-weight: bold;
                        box-shadow: 0 4px 12px rgba(238, 90, 36, 0.4);
                        text-align: center;
                        border: 2px solid white;
                        animation: pulse 2s infinite;
                    ">
                        🏁 ${targetSpaceName}
                    </div>
                    <style>
                        @keyframes pulse {
                            0% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                            100% { transform: scale(1); }
                        }
                    </style>`;
                
                view.Markers.add(endGate.center, flagTemplate, {
                    rank: "always-visible",
                });
            }

            // Focus on the beginning of the route
            view.Camera.focusOn(directionsData.coordinates.slice(0, 3), {
                screenOffsets: { bottom: 50, left: 50, top: 50, right: 50 },
            });

            // Add instruction markers for each step
            directionsData.instructions.forEach((instruction, i) => {
                const nextInstruction = i < directionsData.instructions.length - 1
                    ? directionsData.instructions[i + 1]
                    : null;
                const isLastInstruction = i === directionsData.instructions.length - 1;

                const markerTemplate = `
                    <div style="
                        background: rgba(255, 255, 255, 0.95);
                        padding: 8px 12px;
                        border-radius: 8px;
                        border: 2px solid ${brandColor || '#127E10'};
                        font-size: 12px;
                        font-weight: bold;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                        max-width: 200px;
                        text-align: center;
                        z-index: 1000;
                    ">
                        <p style="margin: 0; color: #333; line-height: 1.4;">
                            ${isLastInstruction
                                ? "🎯 You Arrived!"
                                : `${instruction.action?.type || 'Continue'} ${instruction.action?.bearing || ''}<br/>📏 ${Math.round(nextInstruction?.distance || 0)}m`
                            }
                        </p>
                    </div>`;

                view.Markers.add(instruction.coordinate, markerTemplate, {
                    rank: "always-visible",
                });
            });

            // Create person marker at start position
            const startCoord = directionsData.instructions[0].coordinate;
            const personTemplate = `
                <div style="
                    background: linear-gradient(135deg, #4a7c59, #2d5a2d);
                    color: white;
                    padding: 6px 10px;
                    border-radius: 15px;
                    font-size: 16px;
                    font-weight: bold;
                    box-shadow: 0 3px 10px rgba(74, 124, 89, 0.4);
                    text-align: center;
                    border: 2px solid white;
                ">
                    🚶‍♂️ Starting
                </div>`;
            
            let personMarker = view.Markers.add(startCoord, personTemplate, {
                rank: "always-visible",
            });

            // Wait for markers to render
            await new Promise(resolve => setTimeout(resolve, 500));

            // Animation based on instruction markers (not coordinates)
            let markerIndex = 0;
            const instructions = directionsData.instructions;
            const totalMarkers = instructions.length;
            
            // Calculate timing: divide video duration by number of instruction markers
            const markerInterval = totalMarkers > 1 ? videoDuration / (totalMarkers - 1) : videoDuration;

            console.log(`🎯 Marker-based Animation Details:`);
            console.log(`- Total instruction markers: ${totalMarkers}`);
            console.log(`- Time per marker: ${markerInterval.toFixed(1)}ms (${(markerInterval/1000).toFixed(1)}s)`);
            console.log(`- Total animation time: ${videoDuration}ms`);

            const animateToNextMarker = () => {
                if (!animationStarted || markerIndex >= totalMarkers) {
                    if (markerIndex >= totalMarkers) {
                        console.log("✅ Animation completed!");
                        
                        // Final camera position
                        blueDotInstance.follow("position-and-view", {
                            bearing: 150.7234009824572,
                            pitch: 20.87944187412887,
                            zoomLevel: 20,
                        });

                        // Update person marker to final position
                        if (personMarker) {
                            try {
                                view.Markers.remove(personMarker);
                            } catch (e) {
                                // Ignore cleanup errors
                            }
                        }

                        const finalInstruction = instructions[instructions.length - 1];
                        const finalPersonTemplate = `
                            <div style="
                                background: linear-gradient(135deg, #28a745, #20c997);
                                color: white;
                                padding: 8px 12px;
                                border-radius: 20px;
                                font-size: 16px;
                                font-weight: bold;
                                box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
                                text-align: center;
                                border: 2px solid white;
                                animation: bounce 1s infinite;
                            ">
                                🎉 Arrived at ${targetSpaceName}!
                            </div>
                            <style>
                                @keyframes bounce {
                                    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                                    40% { transform: translateY(-10px); }
                                    60% { transform: translateY(-5px); }
                                }
                            </style>`;
                        
                        personMarker = view.Markers.add(finalInstruction.coordinate, finalPersonTemplate, {
                            rank: "always-visible",
                        });
                    }
                    return;
                }

                const currentInstruction = instructions[markerIndex];
                const progress = (markerIndex / (totalMarkers - 1)) * 100;

                // Update BlueDot position to current instruction marker
                blueDotInstance.update({
                    latitude: currentInstruction.coordinate.latitude,
                    longitude: currentInstruction.coordinate.longitude,
                    floorOrFloorId: currentInstruction.coordinate.floor,
                    accuracy: 1,
                    timestamp: Date.now(),
                });

                // Update person marker position
                if (personMarker) {
                    try {
                        view.Markers.remove(personMarker);
                    } catch (e) {
                        // Ignore cleanup errors
                    }
                }

                const currentProgress = Math.round(progress);
                const isLastMarker = markerIndex === totalMarkers - 1;
                const locationText = isLastMarker ? `Arrived at ${targetSpaceName}!` : `Moving... ${currentProgress}%`;
                
                const updatedPersonTemplate = `
                    <div style="
                        background: linear-gradient(135deg, #4a7c59, #2d5a2d);
                        color: white;
                        padding: 6px 10px;
                        border-radius: 15px;
                        font-size: 14px;
                        font-weight: bold;
                        box-shadow: 0 3px 10px rgba(74, 124, 89, 0.4);
                        text-align: center;
                        border: 2px solid white;
                    ">
                        🚶‍♂️ ${locationText}
                    </div>`;
                
                personMarker = view.Markers.add(currentInstruction.coordinate, updatedPersonTemplate, {
                    rank: "always-visible",
                });

                // Highlight path section up to current marker
                if (markerIndex > 0) {
                    const pathToHighlight = directionsData.coordinates.slice(0, 
                        directionsData.coordinates.findIndex(coord => 
                            coord.latitude === currentInstruction.coordinate.latitude &&
                            coord.longitude === currentInstruction.coordinate.longitude
                        ) + 1
                    );

                    if (pathToHighlight.length > 1) {
                        view.Navigation.highlightPathSection(
                            pathToHighlight[0],
                            pathToHighlight[pathToHighlight.length - 1],
                            {
                                animationDuration: 0,
                                color: "#9d9d9d",
                                widthMultiplier: 1.2,
                            }
                        );
                    }
                }

                console.log(`🚶‍♂️ Marker ${markerIndex + 1}/${totalMarkers} - Progress: ${progress.toFixed(1)}%`);
                markerIndex++;

                // Schedule next marker
                if (markerIndex < totalMarkers) {
                    setTimeout(animateToNextMarker, markerInterval);
                } else {
                    // Final step
                    setTimeout(animateToNextMarker, 100);
                }
            };

            // Start the marker-based animation
            animateToNextMarker();

        } catch (error) {
            console.error('❌ Animation error:', error);
            setAnimationStarted(false);
        }
    };

    const handleStartAnimation = () => {
        if (mapView && mapData && directions && !animationStarted) {
            const targetName = spaceName && spaceName.trim() ? spaceName.trim() : "Kiosk";
            startSynchronizedAnimation(mapView, mapData, targetName, directions);
        }
    };

    const handleResetAnimation = () => {
        setAnimationStarted(false);
        if (blueDot) {
            blueDot.destroy();
            setBlueDot(null);
        }
        if (mapView) {
            mapView.Markers.removeAll();
            if (directions) {
                mapView.Navigation.draw(directions);
            }
        }
    };

    useEffect(() => {
        initMap();

        // Cleanup
        return () => {
            if (blueDot) {
                blueDot.destroy();
            }
        };
    }, []);

    useEffect(() => {
        if (spaceName && mapView && mapData && !animationStarted) {
            setupNavigationPath(mapView, mapData, spaceName);
        }
    }, [spaceName, mapView, mapData]);

    if (error) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                color: '#6c757d',
                textAlign: 'center',
                padding: '20px',
            }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                    Map Loading Error
                </div>
                <div style={{ fontSize: '14px', marginBottom: '20px', maxWidth: '80%' }}>
                    {error}
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                color: '#6c757d',
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: `4px solid ${brandColor || '#127E10'}`,
                    borderTop: '4px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px',
                }} />
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    Loading Synchronized Map...
                </div>
                {spaceName && (
                    <div style={{ fontSize: '14px', marginTop: '10px' }}>
                        Target: <strong>{spaceName}</strong>
                    </div>
                )}
                <style>
                    {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                </style>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {/* Map Container */}
            <div
                id="mappedin-test-map"
                ref={mapRef}
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '15px',
                    overflow: 'hidden',
                }}
            />

            {/* Control Panel */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '15px',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '200px',
            }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
                    Animation Control
                </div>

                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                    📍 Total Markers: <strong>{totalMarkers}</strong>
                </div>

                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                    ⏱️ Video Duration: <strong>{videoDuration / 1000}s</strong>
                </div>

                <div style={{ fontSize: '12px', marginBottom: '15px' }}>
                    🔄 Marker Interval: <strong>{Math.round(markerInterval)}ms</strong>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                        onClick={handleStartAnimation}
                        disabled={animationStarted || !pathVisible}
                        style={{
                            backgroundColor: animationStarted ? '#6c757d' : (brandColor || '#127E10'),
                            color: '#fff',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: animationStarted ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        {animationStarted ? '🎬 Animating...' : '▶️ Start Animation'}
                    </button>

                    <button
                        onClick={handleResetAnimation}
                        disabled={!animationStarted}
                        style={{
                            backgroundColor: !animationStarted ? '#6c757d' : '#dc3545',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: !animationStarted ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        🔄 Reset
                    </button>
                </div>

                {pathVisible && !animationStarted && (
                    <div style={{
                        marginTop: '10px',
                        padding: '8px',
                        backgroundColor: '#e8f5e8',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#2d5a2d',
                    }}>
                        ✅ Path ready for animation
                    </div>
                )}
            </div>
        </div>
    );
};

export default MappedInTestFile;